/**
 * Item Command Implementations for UI-Model-1 (API-Direct Approach)
 *
 * Execute and undo functions for item operations.
 * Operates directly on the nested API structure (container.content.items[]).
 */

import {
  UIModelState,
  ContainerAPI,
  ItemAPI,
  FieldAPI,
  WidgetAPI,
  DirtySection,
  isItemAPI,
  isFieldAPI,
  isWidgetAPI,
  isCategoryAPI,
  isConnectorAPI,
  isSubmodelFull,
  SubmodelFullAPI,
  UsedFieldInfo,
} from '../../types/state.types';
import { NestedContainerItemAPI, ContainerItemAPI } from '../../types/api.types';
import {
  AddItemCommand,
  RemoveItemCommand,
  MoveItemCommand,
  UpdateItemCommand,
  ReorderItemsCommand,
} from '../../types/command.types';
import { generateId } from '../../utils/idGenerator';
import { clearEmptySubmodelRefs } from '../../utils/submodelCleanup';
import { isFreestyle } from '../../selectors';

// ============================================================================
// Type Guards for Content Items (local helpers)
// ============================================================================

/**
 * Type guard to check if a content item is a proper item (not NestedContainerItemAPI)
 */
const isContentItem = (item: ContainerItemAPI): item is ItemAPI => {
  return isItemAPI(item);
};

// ============================================================================
// Helper Functions for Dirty Tracking
// ============================================================================

/**
 * Create updated dirty tracker with new dirty sections
 */
const markCanvasDirty = (
  dirtyTracker: UIModelState['dirtyTracker'],
  canvasId: string
): UIModelState['dirtyTracker'] => {
  const newSections = new Set(Array.from(dirtyTracker.sections));
  newSections.add('canvas' as DirtySection);

  const newModifiedCanvases = new Set(Array.from(dirtyTracker.canvasPages));
  newModifiedCanvases.add(canvasId);

  return {
    ...dirtyTracker,
    sections: newSections,
    canvasPages: newModifiedCanvases,
  };
};

/**
 * Reorder items array by updating order property only for actual items (field/widget)
 * NestedContainerItemAPI items are preserved as-is
 */
const reorderContentItems = (items: ContainerItemAPI[]): ContainerItemAPI[] => {
  let orderIndex = 0;
  return items.map(item => {
    if (isItemAPI(item)) {
      const reordered = { ...item, order: orderIndex };
      orderIndex++;
      return reordered;
    }
    return item;
  });
};

/**
 * Get the order property from an item if it exists
 */
const getItemOrder = (item: ContainerItemAPI): number => {
  if (isItemAPI(item)) {
    return item.order ?? 0;
  }
  return 0;
};

// ============================================================================
// Helper Functions for Nested Structure Manipulation
// ============================================================================

/**
 * Deep clone the model to avoid mutations
 */
const cloneModel = (
  model: UIModelState['model']
): UIModelState['model'] => {
  if (!model) return null;
  return JSON.parse(JSON.stringify(model));
};

/**
 * Clone model and get canvas — shared pattern used by most commands.
 * Returns null if model or canvas not found.
 */
const cloneModelAndGetCanvas = (
  state: UIModelState,
  canvasId: string
): { newModel: NonNullable<UIModelState['model']>; canvas: ReturnType<typeof cloneModel> extends infer M ? M extends { layout: { canvas: Record<string, infer C> } } ? C : never : never } | null => {
  const newModel = cloneModel(state.model);
  if (!newModel) return null;
  const canvas = newModel.layout.canvas[canvasId];
  if (!canvas) return null;
  return { newModel: newModel as NonNullable<UIModelState['model']>, canvas };
};

/**
 * Find a container by ID in the nested structure
 */
const findContainerById = (
  containers: ContainerAPI[],
  containerId: string
): ContainerAPI | undefined => {
  for (const container of containers) {
    if (container.id === containerId) {
      return container;
    }
    if (container.content.nestedContainers) {
      const found = findContainerById(container.content.nestedContainers, containerId);
      if (found) return found;
    }
  }
  return undefined;
};

/**
 * Update a container in the nested structure
 * Exported for use in ConfigPanel and other components that need to update nested containers
 */
export const updateContainerInList = (
  containers: ContainerAPI[],
  containerId: string,
  updater: (container: ContainerAPI) => ContainerAPI
): ContainerAPI[] => {
  return containers.map(container => {
    if (container.id === containerId) {
      return updater(container);
    }

    if (container.content.nestedContainers) {
      return {
        ...container,
        content: {
          ...container.content,
          nestedContainers: updateContainerInList(
            container.content.nestedContainers,
            containerId,
            updater
          ),
        },
      };
    }

    return container;
  });
};

/**
 * Find item in nested container structure
 * Returns [containerId, item, itemIndex] if found
 */
const findItemInContainers = (
  containers: ContainerAPI[],
  itemId: string
): [string, ItemAPI, number] | null => {
  for (const container of containers) {
    const itemIndex = (container.content.items || []).findIndex(
      (item): item is ItemAPI => isItemAPI(item) && item.id === itemId
    );

    if (itemIndex !== -1) {
      const foundItem = (container.content.items || [])[itemIndex];
      if (isItemAPI(foundItem)) {
        return [container.id, foundItem, itemIndex];
      }
    }

    if (container.content.nestedContainers) {
      const result = findItemInContainers(container.content.nestedContainers, itemId);
      if (result) return result;
    }
  }
  return null;
};

// ============================================================================
// Container Item Manipulation Helpers
// ============================================================================

/** Add an item to a container at the specified index, reorder remaining items */
const addItemToContainer = (
  containers: ContainerAPI[],
  containerId: string,
  item: ItemAPI,
  index?: number
): ContainerAPI[] => {
  return updateContainerInList(containers, containerId, container => {
    const newItems = [...(container.content.items || [])];
    newItems.splice(index ?? newItems.length, 0, item);
    return {
      ...container,
      content: { ...container.content, items: reorderContentItems(newItems) },
    };
  });
};

/** Remove an item from a container by ID, reorder remaining items */
const removeItemFromContainer = (
  containers: ContainerAPI[],
  containerId: string,
  itemId: string
): ContainerAPI[] => {
  return updateContainerInList(containers, containerId, container => {
    const newItems = (container.content.items || []).filter(
      item => !isItemAPI(item) || item.id !== itemId
    );
    return {
      ...container,
      content: { ...container.content, items: reorderContentItems(newItems) },
    };
  });
};

// ============================================================================
// Uniqueness Validation
// ============================================================================

/**
 * Generate a unique key for a component (field, category, or connector) based on its origin.
 * For submodel-scoped items: `${parentId}-${identifier}`
 * For root items: `${identifier}`
 * Must match the key generation in UIModelStateContext.tsx
 */
const getComponentKey = (identifier: string, parentId?: string): string => {
  return parentId ? `${parentId}-${identifier}` : identifier;
};

interface UniquenessCheck {
  typeGuard: (item: ItemAPI) => boolean;
  getKey: (item: ItemAPI) => string;
  getMap: (state: UIModelState) => Map<string, UsedFieldInfo>;
  typeName: string;
}

const UNIQUENESS_CHECKS: UniquenessCheck[] = [
  {
    typeGuard: isFieldAPI,
    getKey: (item) => getComponentKey((item as FieldAPI).name, (item as FieldAPI).parentId),
    getMap: (s) => s.usedFieldsMap,
    typeName: 'Field',
  },
  {
    typeGuard: isCategoryAPI,
    getKey: (item) => {
      const cat = item as unknown as { categoryId: string; parentId?: string };
      return getComponentKey(cat.categoryId, cat.parentId);
    },
    getMap: (s) => s.usedCategoriesMap,
    typeName: 'Category',
  },
  {
    typeGuard: isConnectorAPI,
    getKey: (item) => getComponentKey((item as FieldAPI).name, (item as FieldAPI).parentId),
    getMap: (s) => s.usedConnectorsMap,
    typeName: 'Connector',
  },
];

/**
 * Validate that an item can be placed in a container (global uniqueness).
 *
 * For ADD operations: any existing usage = blocked.
 * For MOVE operations: usage in a container other than the source = blocked.
 *
 * @param item - The item to validate
 * @param state - Current UI model state with usage maps
 * @param targetContainerId - The container where the item will be placed
 * @param callerName - Name for console.warn messages
 * @param allowContainerId - Container where existing usage is OK (for moves: the source container)
 * @returns true if the item can be placed, false if blocked
 */
const validateItemUniqueness = (
  item: ItemAPI,
  state: UIModelState,
  targetContainerId: string,
  callerName: string,
  allowContainerId?: string
): boolean => {
  // Free style: global uniqueness (Rule 5) doesn't apply — duplicates are
  // allowed (the UI confirms them and stamps instance-suffixed ids).
  if (isFreestyle(state.model)) return true;

  for (const check of UNIQUENESS_CHECKS) {
    if (!check.typeGuard(item)) continue;

    const key = check.getKey(item);
    const existingUsage = check.getMap(state).get(key);
    if (!existingUsage) continue;

    // If there's an "allowed" container (move source), usage there is OK
    if (allowContainerId && existingUsage.containerId === allowContainerId) continue;

    // Blocked — emit appropriate warning
    if (existingUsage.containerId !== targetContainerId) {
      console.warn(
        `[${callerName}] BLOCKED: ${check.typeName} "${item.name}" already used in container "${existingUsage.containerName}"` +
          (existingUsage.pageName ? ` on page "${existingUsage.pageName}"` : '')
      );
    } else {
      console.warn(
        `[${callerName}] BLOCKED: ${check.typeName} "${item.name}" already exists in this container`
      );
    }
    return false;
  }
  return true;
};

// ============================================================================
// Add Item
// ============================================================================

export const executeAddItem = (
  state: UIModelState,
  command: AddItemCommand
): UIModelState => {
  if (!state.model) return state;

  const { item, canvasId, containerId, insertIndex } = command.payload;

  // Validate uniqueness (field, category, or connector)
  if (!validateItemUniqueness(item, state, containerId, 'AddItem')) {
    return state;
  }

  // Clone model and get canvas
  const result = cloneModelAndGetCanvas(state, canvasId);
  if (!result) return state;
  const { newModel, canvas } = result;

  // Create item ID if not provided
  const itemId = item.id || generateId('item');
  const itemOrder = insertIndex ?? 0;

  const newItem: ItemAPI = {
    ...item,
    id: itemId,
    order: itemOrder,
  };

  // Add item to container
  canvas.containers = addItemToContainer(canvas.containers, containerId, newItem, insertIndex);

  return {
    ...state,
    model: newModel,
    dirtyTracker: markCanvasDirty(state.dirtyTracker, canvasId),
  };
};

export const undoAddItem = (
  state: UIModelState,
  command: AddItemCommand
): UIModelState => {
  if (!state.model) return state;

  const { canvasId, containerId } = command.payload;
  const itemId = command.payload.item.id;
  if (!itemId) return state;

  // Clone model and get canvas
  const result = cloneModelAndGetCanvas(state, canvasId);
  if (!result) return state;
  const { newModel, canvas } = result;

  // Remove the item from container
  canvas.containers = removeItemFromContainer(canvas.containers, containerId, itemId);

  return {
    ...state,
    model: newModel,
    dirtyTracker: markCanvasDirty(state.dirtyTracker, canvasId),
  };
};

// ============================================================================
// Remove Item
// ============================================================================

export const executeRemoveItem = (
  state: UIModelState,
  command: RemoveItemCommand
): UIModelState => {
  if (!state.model) return state;

  const { itemId, canvasId, containerId } = command.payload;

  // Clone model and get canvas
  const result = cloneModelAndGetCanvas(state, canvasId);
  if (!result) return state;
  const { newModel, canvas } = result;

  // Find and store the item for undo
  const container = findContainerById(canvas.containers, containerId);
  if (!container) return state;

  const itemIndex = (container.content.items || []).findIndex(
    (item): item is ItemAPI => isItemAPI(item) && item.id === itemId
  );
  if (itemIndex === -1) return state;

  const removedItem = (container.content.items || [])[itemIndex];
  if (!isItemAPI(removedItem)) return state;

  // Store for undo
  command.payload.removedItem = removedItem;
  command.payload.removedIndex = itemIndex;

  // Remove item from container
  canvas.containers = removeItemFromContainer(canvas.containers, containerId, itemId);

  // Clear submodel refs from containers whose last DIRECT submodel item went.
  // Stash the source container's binding first so undo can restore it — under
  // the direct-items rule only this container's binding can change here.
  if (removedItem.parentId) {
    command.payload.previousSubmodel = container.content.submodel;
    command.payload.previousSubmodels = container.content.submodels;
    canvas.containers = clearEmptySubmodelRefs(canvas.containers);
  }

  return {
    ...state,
    model: newModel,
    dirtyTracker: markCanvasDirty(state.dirtyTracker, canvasId),
  };
};

export const undoRemoveItem = (
  state: UIModelState,
  command: RemoveItemCommand
): UIModelState => {
  if (!state.model) return state;

  const { canvasId, containerId, removedItem, removedIndex } = command.payload;

  if (!removedItem) return state;

  // Clone model and get canvas
  const result = cloneModelAndGetCanvas(state, canvasId);
  if (!result) return state;
  const { newModel, canvas } = result;

  // Add item back to container at original position
  const insertIndex = removedIndex ?? removedItem.order ?? undefined;
  canvas.containers = addItemToContainer(canvas.containers, containerId, removedItem, insertIndex);

  // Restore the binding the removal's cleanup may have released (no-op when
  // it didn't change — the stash holds the pre-removal values).
  const { previousSubmodel, previousSubmodels } = command.payload;
  if (previousSubmodel !== undefined || previousSubmodels !== undefined) {
    canvas.containers = updateContainerInList(canvas.containers, containerId, c => ({
      ...c,
      content: {
        ...c.content,
        submodel: previousSubmodel,
        submodels: previousSubmodels,
      },
    }));
  }

  return {
    ...state,
    model: newModel,
    dirtyTracker: markCanvasDirty(state.dirtyTracker, canvasId),
  };
};

// ============================================================================
// Update Item
// ============================================================================

export const executeUpdateItem = (
  state: UIModelState,
  command: UpdateItemCommand
): UIModelState => {
  if (!state.model) return state;

  const { itemId, canvasId, containerId, updates } = command.payload;

  // Clone model and get canvas
  const result = cloneModelAndGetCanvas(state, canvasId);
  if (!result) return state;
  const { newModel, canvas } = result;

  // Store previous values for undo
  let previousValues: Partial<ItemAPI> | undefined;

  // Update the item in container
  canvas.containers = updateContainerInList(canvas.containers, containerId, container => {
    const newItems = (container.content.items || []).map(item => {
      // Only update actual items (field/widget), not NestedContainerAPI
      if (!isItemAPI(item) || item.id !== itemId) return item;

      // Store previous values
      previousValues = {
        name: item.name,
        order: item.order,
        configuration: item.configuration ? { ...item.configuration } : undefined,
      };

      // Apply updates - safely access properties that may not exist on all item types
      const updatedItem = {
        ...item,
        name: updates.name ?? item.name,
        order: updates.order ?? item.order,
        configuration: updates.configuration
          ? { ...item.configuration, ...updates.configuration }
          : item.configuration,
      } as ItemAPI;

      return updatedItem;
    });

    return {
      ...container,
      content: {
        ...container.content,
        items: newItems,
      },
    };
  });

  command.payload.previousValues = previousValues;

  return {
    ...state,
    model: newModel,
    dirtyTracker: markCanvasDirty(state.dirtyTracker, canvasId),
  };
};

export const undoUpdateItem = (
  state: UIModelState,
  command: UpdateItemCommand
): UIModelState => {
  if (!state.model) return state;

  const { itemId, canvasId, containerId, previousValues } = command.payload;

  if (!previousValues) return state;

  // Clone model and get canvas
  const result = cloneModelAndGetCanvas(state, canvasId);
  if (!result) return state;
  const { newModel, canvas } = result;

  // Restore previous values
  canvas.containers = updateContainerInList(canvas.containers, containerId, container => {
    const newItems = (container.content.items || []).map(item => {
      // Only update actual items (field/widget), not NestedContainerAPI
      if (!isItemAPI(item) || item.id !== itemId) return item;

      const restoredItem = {
        ...item,
        name: previousValues.name ?? item.name,
        order: previousValues.order ?? item.order,
        configuration: previousValues.configuration
          ? { ...item.configuration, ...previousValues.configuration }
          : item.configuration,
      } as ItemAPI;

      return restoredItem;
    });

    return {
      ...container,
      content: {
        ...container.content,
        items: newItems,
      },
    };
  });

  return {
    ...state,
    model: newModel,
    dirtyTracker: markCanvasDirty(state.dirtyTracker, canvasId),
  };
};

// ============================================================================
// Move Item (supports cross-container movement)
// ============================================================================

export const executeMoveItem = (
  state: UIModelState,
  command: MoveItemCommand
): UIModelState => {
  if (!state.model) return state;

  const {
    itemId,
    fromCanvasId,
    toCanvasId,
    fromContainerId,
    toContainerId,
    toIndex,
  } = command.payload;

  // Validate uniqueness for cross-container moves
  if (fromContainerId !== toContainerId) {
    const fromCanvas = state.model.layout.canvas[fromCanvasId];
    if (fromCanvas) {
      const sourceContainer = findContainerById(fromCanvas.containers, fromContainerId);
      if (sourceContainer) {
        const itemToMove = (sourceContainer.content.items || []).find(
          item => isItemAPI(item) && item.id === itemId
        );

        if (itemToMove && isItemAPI(itemToMove)) {
          if (!validateItemUniqueness(itemToMove, state, toContainerId, 'MoveItem', fromContainerId)) {
            return state;
          }
        }
      }
    }
  }

  // Clone the model
  const newModel = cloneModel(state.model);
  if (!newModel) return state;

  // Get source canvas
  const fromCanvas = newModel.layout.canvas[fromCanvasId];
  if (!fromCanvas) return state;

  // Find item in source, then remove it
  const itemResult = findItemInContainers(fromCanvas.containers, itemId);
  if (!itemResult) return state;
  const [, movedItem] = itemResult;

  fromCanvas.containers = removeItemFromContainer(fromCanvas.containers, fromContainerId, itemId);

  // Get target canvas
  const toCanvas = newModel.layout.canvas[toCanvasId];
  if (!toCanvas) return state;

  // Add item to target container
  const updatedMovedItem: ItemAPI = {
    ...movedItem,
    order: toIndex ?? 0,
  };
  toCanvas.containers = addItemToContainer(toCanvas.containers, toContainerId, updatedMovedItem, toIndex);

  // Handle SubmodelFullAPI when moving a submodel item cross-container
  if (movedItem.parentId && fromContainerId !== toContainerId) {
    // Look up source container to copy SubmodelFullAPI to target
    const lookupContainers = fromCanvasId === toCanvasId
      ? toCanvas.containers
      : fromCanvas.containers;
    const sourceContainer = findContainerById(lookupContainers, fromContainerId);

    if (sourceContainer?.content?.submodel && isSubmodelFull(sourceContainer.content.submodel)) {
      const sourceSubmodel = sourceContainer.content.submodel;

      // Copy SubmodelFullAPI to target container (if it doesn't already have one)
      toCanvas.containers = updateContainerInList(toCanvas.containers, toContainerId, container => {
        if (!container.content.submodel) {
          return {
            ...container,
            content: { ...container.content, submodel: { ...sourceSubmodel } },
          };
        }
        return container;
      });
    }

    // Conditionally clear source (only if no items remain for submodel)
    if (fromCanvasId === toCanvasId) {
      toCanvas.containers = clearEmptySubmodelRefs(toCanvas.containers);
    } else {
      fromCanvas.containers = clearEmptySubmodelRefs(fromCanvas.containers);
    }
  }

  // Update dirty tracking for both canvases
  let newDirtyTracker = markCanvasDirty(state.dirtyTracker, fromCanvasId);
  if (fromCanvasId !== toCanvasId) {
    newDirtyTracker = markCanvasDirty(newDirtyTracker, toCanvasId);
  }

  return {
    ...state,
    model: newModel,
    dirtyTracker: newDirtyTracker,
  };
};

export const undoMoveItem = (
  state: UIModelState,
  command: MoveItemCommand
): UIModelState => {
  // Reverse the move by swapping from/to
  const reverseCommand: MoveItemCommand = {
    ...command,
    payload: {
      itemId: command.payload.itemId,
      fromCanvasId: command.payload.toCanvasId,
      toCanvasId: command.payload.fromCanvasId,
      fromContainerId: command.payload.toContainerId,
      toContainerId: command.payload.fromContainerId,
      fromIndex: command.payload.toIndex,
      toIndex: command.payload.fromIndex,
    },
  };

  return executeMoveItem(state, reverseCommand);
};

// ============================================================================
// Reorder Items
// ============================================================================

export const executeReorderItems = (
  state: UIModelState,
  command: ReorderItemsCommand
): UIModelState => {
  if (!state.model) return state;

  const { canvasId, containerId, itemIds } = command.payload;

  // Clone model and get canvas
  const result = cloneModelAndGetCanvas(state, canvasId);
  if (!result) return state;
  const { newModel, canvas } = result;

  // Store previous order for undo
  const container = findContainerById(canvas.containers, containerId);
  if (!container) return state;

  command.payload.previousOrder = (container.content.items || [])
    .filter((item): item is ItemAPI => isItemAPI(item))
    .map(item => item.id);

  // Reorder items in container
  canvas.containers = updateContainerInList(canvas.containers, containerId, cont => {
    // Separate actual items from NestedContainerAPI entries
    const actualItems = (cont.content.items || []).filter(isItemAPI);
    const nestedContainerItems = (cont.content.items || []).filter(item => !isItemAPI(item));

    // Build a map of existing actual items
    const itemMap = new Map(actualItems.map(item => [item.id, item]));

    // Create new ordered array of actual items
    const reorderedItems: ItemAPI[] = itemIds
      .map(id => itemMap.get(id))
      .filter((item): item is ItemAPI => item !== undefined)
      .map((item, index) => ({ ...item, order: index }));

    // Add any actual items not in the new order at the end (shouldn't happen normally)
    const orderedIds = new Set(itemIds);
    const remainingItems: ItemAPI[] = actualItems
      .filter(item => !orderedIds.has(item.id))
      .map((item, index) => ({ ...item, order: reorderedItems.length + index }));

    // Combine reordered items with NestedContainerAPI entries
    const allItems: ContainerItemAPI[] = [...reorderedItems, ...remainingItems, ...nestedContainerItems];

    return {
      ...cont,
      content: {
        ...cont.content,
        items: allItems,
      },
    };
  });

  return {
    ...state,
    model: newModel,
    dirtyTracker: markCanvasDirty(state.dirtyTracker, canvasId),
  };
};

export const undoReorderItems = (
  state: UIModelState,
  command: ReorderItemsCommand
): UIModelState => {
  const { previousOrder } = command.payload;

  if (!previousOrder) return state;

  // Reorder back to previous order
  const reverseCommand: ReorderItemsCommand = {
    ...command,
    payload: {
      ...command.payload,
      itemIds: previousOrder,
      previousOrder: command.payload.itemIds,
    },
  };

  return executeReorderItems(state, reverseCommand);
};
